import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { V1CloudEventsResourceRow } from '@ariksa/audittrail/api';
import { Box, HStack, Stack, Td, Tr } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { StackedCell, WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { DownIcon, RightIcon } from 'components/Icons';
import { EllipsisText, PlusCountTag, renderTime, Tag } from 'components/index';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';

interface Props {
  currentResource?: V1CloudEventsResourceRow;
  dateRange?: DateRange;
  objectPaths?: Array<{ value: string }>;
}

type ActivityAccessRow = Record<string, any>;

const getNestedMechanisms = (row?: ActivityAccessRow): ActivityAccessRow[] =>
  Array.isArray(row?.mechanisms) ? row.mechanisms : [];

const getMechanismName = (row?: ActivityAccessRow) =>
  row?.mechanism ?? row?.mechanism_name;

const getMechanismType = (row?: ActivityAccessRow) =>
  row?.mechanism_type ?? row?.mechanism_agnostic_name;

const hasMechanism = (row?: ActivityAccessRow) =>
  getMechanismName(row) !== undefined && getMechanismName(row) !== null;

const isSameMechanism = (
  first?: ActivityAccessRow,
  second?: ActivityAccessRow,
) =>
  getMechanismName(first) === getMechanismName(second) &&
  getMechanismType(first) === getMechanismType(second);

const getFirstMechanism = (row: ActivityAccessRow): ActivityAccessRow => {
  const mechanisms = getNestedMechanisms(row);

  if (!mechanisms.length) return row;

  // Support both response shapes: an aggregated row may contain all
  // mechanisms, while another response can keep the first mechanism on the
  // parent and return only the additional mechanisms in `mechanisms`.
  if (hasMechanism(row) && !isSameMechanism(row, mechanisms[0])) {
    return row;
  }

  return mechanisms[0];
};

const getAdditionalMechanisms = (row: ActivityAccessRow) => {
  const mechanisms = getNestedMechanisms(row);

  if (!mechanisms.length) return [];

  return hasMechanism(row) && !isSameMechanism(row, mechanisms[0])
    ? mechanisms
    : mechanisms.slice(1);
};

const getRowKey = (row: ActivityAccessRow) =>
  String(
    row?.identity_id ?? `${row?.identity_type ?? ''}:${row?.identity ?? ''}`,
  );

const getFolderNames = (row?: ActivityAccessRow): string[] => {
  const folderList = row?.folders ?? row?.folder_names ?? row?.folder_paths;
  const folders = Array.isArray(folderList)
    ? folderList
    : folderList
    ? [folderList]
    : row?.folder_name
    ? [row.folder_name]
    : [];

  return folders
    .map(folder =>
      typeof folder === 'string'
        ? folder
        : folder?.folder_name ?? folder?.name ?? folder?.path ?? folder?.value,
    )
    .filter(Boolean)
    .map(String);
};

export const ActivityGraphTable: FC<Props> = ({
  currentResource,
  dateRange,
  objectPaths,
}) => {
  const dispatch = useDispatch();
  const { getResourceAlias } = useResourceType();
  const { activityAccesses } = useSelector(selectIncidentResponse);
  const pageNumber = activityAccesses.page.info.page_number;
  const pageSize = activityAccesses.page.info.page_size;
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [finalSearchTerm, setFinalSearchTerm] = useState('');
  const objectPathValues = useMemo(
    () => objectPaths?.map(option => option.value) ?? [],
    [objectPaths],
  );
  const queryKey = JSON.stringify([
    currentResource?.resource_arn,
    dateRange?.from,
    dateRange?.to,
    objectPathValues,
    finalSearchTerm,
  ]);
  const previousQueryKey = useRef(queryKey);

  useEffect(() => {
    setExpandedRowKey(null);
  }, [activityAccesses.data]);

  useEffect(() => {
    const queryChanged = previousQueryKey.current !== queryKey;
    if (queryChanged) {
      previousQueryKey.current = queryKey;
      if (pageNumber !== 1) {
        dispatch(
          actions.updateActivityAccessesPagination({
            page_number: 1,
            page_size: pageSize,
          }),
        );
        return;
      }
    }

    dispatch(
      actions.getActivityAccesses({
        q: {
          body: {
            start_date: dateRange?.from
              ? dayjs(dateRange.from).toISOString()
              : undefined,
            end_date: dateRange?.to
              ? dayjs(dateRange.to).toISOString()
              : undefined,
            resource_arn: currentResource?.resource_arn,
            folders: objectPathValues,
            search_term: finalSearchTerm || undefined,
            limit: pageSize,
            offset: (pageNumber - 1) * pageSize,
          },
        },
      }),
    );
  }, [
    currentResource?.resource_arn,
    dateRange?.from,
    dateRange?.to,
    dispatch,
    finalSearchTerm,
    objectPathValues,
    pageNumber,
    pageSize,
    queryKey,
  ]);

  const isAggregated = (row: ActivityAccessRow) =>
    getAdditionalMechanisms(row).length > 0;

  const toggleRow = (row: ActivityAccessRow) => {
    if (!isAggregated(row)) return;

    const rowKey = getRowKey(row);
    setExpandedRowKey(previousKey => (previousKey === rowKey ? null : rowKey));
  };

  const renderMechanism = (row: ActivityAccessRow) => (
    <StackedCell
      upper={getMechanismName(row) ?? '-'}
      lower={getResourceAlias(getMechanismType(row)) ?? '-'}
    />
  );

  const renderFolders = (row: ActivityAccessRow) => {
    const folderNames = getFolderNames(row);
    const visibleFolders = folderNames.slice(0, 4);
    const remainingFolders = folderNames.slice(4);

    if (!folderNames.length) return '-';

    return (
      <HStack w="full" flexWrap="wrap" spacing={1}>
        {visibleFolders.map(folderName => (
          <Tag
            key={folderName}
            label={<EllipsisText label={folderName} />}
            styles={{
              tag: {
                py: 0.5,
                bg: colorOpacity(customTheme.colors.primary, 0.1),
              },
            }}
          />
        ))}
        {remainingFolders.length > 0 && (
          <PlusCountTag
            count={remainingFolders.length}
            tooltip={remainingFolders.join(', ')}
          />
        )}
      </HStack>
    );
  };

  const renderMechanismBreakdown = ({ row }) => {
    const mechanisms = getAdditionalMechanisms(row);

    if (!mechanisms.length || getRowKey(row) !== expandedRowKey) return null;

    return (
      <>
        {mechanisms.map((mechanism: ActivityAccessRow, index: number) => (
          <Tr key={`${getRowKey(row)}-mechanism-${index}`}>
            <Td />
            <Td textAlign="left">
              <Box pl={6}>{renderMechanism(mechanism)}</Box>
            </Td>
            <Td textAlign="left">{renderFolders(mechanism)}</Td>
            <Td textAlign="left">
              {renderTime({
                value:
                  mechanism?.last_activity_start ?? row.last_activity_start,
              })}
            </Td>
          </Tr>
        ))}
      </>
    );
  };

  const columns: TableColumnProps[] = [
    {
      header: <Box pl={16}>Identity</Box>,
      render: ({ row }) => {
        const isOpen = getRowKey(row) === expandedRowKey;
        const aggregated = isAggregated(row);

        return (
          <HStack spacing={2} justify="flex-start">
            <Box flexShrink={0} minW={3}>
              {aggregated &&
                (isOpen ? (
                  <DownIcon color="gray.500" boxSize={3} />
                ) : (
                  <RightIcon color="gray.500" boxSize={3} />
                ))}
            </Box>
            <WithResourceIcon
              resourceType={row.identity_type!}
              hStackStyles={{ flex: 1, minW: 0 }}
            >
              <StackedCell
                upper={row.identity ?? '-'}
                lower={getResourceAlias(row.identity_type) ?? '-'}
              />
            </WithResourceIcon>
          </HStack>
        );
      },
    },
    {
      header: 'Mechanism',
      render: ({ row }) => renderMechanism(getFirstMechanism(row)),
      align: 'left',
    },
    {
      header: 'Folders',
      render: ({ row }) => renderFolders(getFirstMechanism(row)),
      align: 'left',
    },
    {
      header: 'Last Activity',
      render: ({ row }) => renderTime({ value: row.last_activity_start }),
      align: 'left',
    },
  ];

  return (
    <Stack flex={1} w="full" h="full" spacing={5}>
      <Box w={64}>
        <SearchBox
          value={searchTerm}
          onChange={value => {
            setSearchTerm(value);
            if (!value) setFinalSearchTerm('');
          }}
          onSearch={() => setFinalSearchTerm(searchTerm.trim())}
          onClickClose={() => {
            setSearchTerm('');
            setFinalSearchTerm('');
          }}
          placeholder="Search identity..."
        />
      </Box>
      <Box flex={1} minH={0} w="full">
        <Table
          columns={columns}
          data={activityAccesses.data as Record<string, any>[]}
          isError={activityAccesses.isError}
          isLoading={activityAccesses.isLoading}
          pagination={{
            pageInfo: activityAccesses.page.info,
            onChange: info =>
              dispatch(actions.updateActivityAccessesPagination(info)),
            totalCount: activityAccesses.page.totalCount,
          }}
          expanded
          subComponent={renderMechanismBreakdown}
          onRowClick={toggleRow}
          cursor={(row: ActivityAccessRow) =>
            isAggregated(row) ? 'pointer' : 'default'
          }
        />
      </Box>
    </Stack>
  );
};
